import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, AlertTriangle, CheckCircle2, ClipboardCheck, Users, BookOpen, ChevronDown, Brain, Clock, Mail } from 'lucide-react';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import Toast from './Toast';
import ComiteTIPrismeModal from './ComiteTIPrismeModal';
import type { ComiteDashboardClientSummary, ComiteDashboardResponse } from '../services/comiteService';
import { getComiteDashboard, setComiteClientResponsable } from '../services/comiteService';
import { useLexLastSync } from '../contexts/LexLastSyncContext';
import { useAuth } from '../contexts/AuthContext';
import { getPartiesPrenantes, type PartiePrenante } from '../services/partiesPrenantesService';

interface ComiteTIClientsDashboardModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EMAIL_TEST_ALLOWED_EMAIL = 'jean-michel.caouette@bzinc.ca';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export default function ComiteTIClientsDashboardModal({ isOpen, onClose }: ComiteTIClientsDashboardModalProps) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ComiteDashboardResponse | null>(null);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [selectedClient, setSelectedClient] = useState<{ id: number; nom: string } | null>(null);
  const [showGuide, setShowGuide] = useState(false);
  const [showEmailTestModal, setShowEmailTestModal] = useState(false);
  const [partiesPrenantesBz, setPartiesPrenantesBz] = useState<PartiePrenante[]>([]);
  const [emailTestRecipientId, setEmailTestRecipientId] = useState<number | ''>('');
  const [emailTestSubject, setEmailTestSubject] = useState('Test Comité TI - PRIsME');
  const [emailTestBody, setEmailTestBody] = useState('');
  const [emailTestIncludeTable, setEmailTestIncludeTable] = useState(true);
  const [emailTestSending, setEmailTestSending] = useState(false);
  const { dates: lexDates } = useLexLastSync();
  const { user } = useAuth();
  const lastAnalysisDate = isOpen ? (lexDates['comite_ti_dashboard_clients_last_ai_analysis'] ?? null) : null;
  const showEmailTestOption = user?.email?.toLowerCase() === EMAIL_TEST_ALLOWED_EMAIL.toLowerCase();

  useEffect(() => {
    if (!isOpen) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    getPartiesPrenantes(2)
      .then((list) => {
        if (!cancelled) setPartiesPrenantesBz(list || []);
      })
      .catch(() => {
        if (!cancelled) setPartiesPrenantesBz([]);
      });
    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const buildComiteTiTableHtml = (): string => {
    if (!data?.clients?.length) return '';
    const t = data.totals;
    const rows = data.clients
      .slice()
      .sort((a, b) => {
        if (b.a_valider !== a.a_valider) return b.a_valider - a.a_valider;
        return a.client_nom.localeCompare(b.client_nom, 'fr');
      })
      .map(
        (c) =>
          `<tr>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">${escapeHtml(c.client_nom)}</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;text-align:center;">${c.en_cours}</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;text-align:center;">${c.completees}</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;text-align:center;">${c.valides}</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;text-align:center;">${c.a_valider}</td>
          </tr>`
      )
      .join('');
    return `
      <div style="margin-top:16px;">
        <h3 style="font-size:16px;margin-bottom:8px;">📊 Dashboard Comité TI - Clients</h3>
        <p style="font-size:13px;color:#6b7280;margin-bottom:8px;">Vue consolidée : en cours, complétées, validées, à valider.</p>
        <table style="border-collapse:collapse;width:100%;max-width:600px;font-size:13px;">
          <thead>
            <tr style="background:#f3f4f6;">
              <th style="padding:8px 12px;border:1px solid #e5e7eb;text-align:left;">Client</th>
              <th style="padding:8px 12px;border:1px solid #e5e7eb;text-align:center;">En cours</th>
              <th style="padding:8px 12px;border:1px solid #e5e7eb;text-align:center;">Complétées</th>
              <th style="padding:8px 12px;border:1px solid #e5e7eb;text-align:center;">Validées</th>
              <th style="padding:8px 12px;border:1px solid #e5e7eb;text-align:center;">À valider</th>
            </tr>
          </thead>
          <tbody>
            ${rows}
          </tbody>
          <tfoot>
            <tr style="background:#eef2ff;font-weight:bold;">
              <td style="padding:8px 12px;border:1px solid #e5e7eb;">Total</td>
              <td style="padding:8px 12px;border:1px solid #e5e7eb;text-align:center;">${t?.en_cours ?? 0}</td>
              <td style="padding:8px 12px;border:1px solid #e5e7eb;text-align:center;">${t?.completees ?? 0}</td>
              <td style="padding:8px 12px;border:1px solid #e5e7eb;text-align:center;">${t?.valides ?? 0}</td>
              <td style="padding:8px 12px;border:1px solid #e5e7eb;text-align:center;">${t?.a_valider ?? 0}</td>
            </tr>
          </tfoot>
        </table>
      </div>`;
  };

  const sendTestEmail = async () => {
    const pp = partiesPrenantesBz.find((p) => p.id === emailTestRecipientId);
    if (!pp?.email) {
      setToast({ message: 'Choisissez une partie prenante BZ avec une adresse courriel.', type: 'error' });
      return;
    }
    let bodyHtml = emailTestBody.trim() ? `<p>${emailTestBody.trim().replace(/\n/g, '<br>')}</p>` : '';
    if (emailTestIncludeTable) {
      bodyHtml += buildComiteTiTableHtml();
    }
    if (!bodyHtml.trim()) bodyHtml = '<p>Courriel de test — PRIsME BZ.</p>';
    setEmailTestSending(true);
    try {
      const res = await fetch('/api/auth/microsoft/send-test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          toEmail: pp.email,
          subject: emailTestSubject.trim() || 'Test Comité TI - PRIsME',
          body: bodyHtml,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.details ? `${json?.error || 'Erreur'} — ${json.details}` : (json?.error || 'Erreur lors de l\'envoi');
        setToast({ message: msg, type: 'error' });
        return;
      }
      setToast({ message: json?.message || 'Courriel envoyé.', type: 'success' });
      setShowEmailTestModal(false);
      setEmailTestRecipientId('');
      setEmailTestBody('');
    } catch (e) {
      setToast({ message: 'Erreur réseau ou serveur.', type: 'error' });
    } finally {
      setEmailTestSending(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const d = await getComiteDashboard();
      setData(d);
    } catch (e) {
      console.error(e);
      setToast({ message: 'Erreur chargement dashboard', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const clientsSorted = useMemo(() => {
    const all = data?.clients || [];
    const q = search.trim().toLowerCase();
    const filtered = q ? all.filter((c) => c.client_nom.toLowerCase().includes(q)) : all;
    return filtered.sort((a, b) => {
      // 8. ceux à valider en premier, ensuite alphabétique
      if (b.a_valider !== a.a_valider) return b.a_valider - a.a_valider;
      return a.client_nom.localeCompare(b.client_nom, 'fr');
    });
  }, [data?.clients, search]);

  if (!isOpen) return null;

  const totals = data?.totals;

  return (
    <>
      <Toast
        message={toast?.message || ''}
        type={toast?.type || 'success'}
        isVisible={!!toast}
        onClose={() => setToast(null)}
      />

      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-[1100px] max-h-[95vh] overflow-hidden flex flex-col"
        >
          <div className="bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 text-white p-6">
            <div className="flex justify-between items-start gap-4">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-3xl font-bold">📊 Dashboard Comité TI - Clients</h2>
                  <span className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-400/25 border border-amber-200/50 rounded-full text-xs font-semibold text-amber-50" title="Disponible dans AI Assistant">
                    <Brain className="w-4 h-4 text-amber-200 shrink-0" strokeWidth={2.5} />
                    <span className="text-white font-bold tracking-wide">AI</span>
                  </span>
                </div>
                <p className="text-white/80 text-sm">
                  Vue consolidée: en cours (planifiées + en cours), complétées, validées et à valider.
                </p>
                {lastAnalysisDate && (
                  <p className="text-white/70 text-xs mt-2 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    Dernière synchro LEX: {formatLastAnalysisDate(lastAnalysisDate)}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {showEmailTestOption && (
                  <button
                    onClick={() => setShowEmailTestModal(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 transition-colors text-sm font-semibold text-white"
                  >
                    <Mail className="w-4 h-4" />
                    Envoyer un courriel (test)
                  </button>
                )}
                <button
                  onClick={() => setShowGuide((v) => !v)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors text-sm font-semibold ${
                    showGuide ? 'bg-white/25 border-white/40 text-white' : 'bg-white/10 hover:bg-white/20 border-white/20 text-white'
                  }`}
                >
                  <BookOpen className="w-4 h-4" />
                  Guide
                </button>
                <button
                  onClick={load}
                  className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 transition-colors text-sm font-semibold"
                >
                  Rafraîchir
                </button>
                <button onClick={onClose} className="hover:bg-white/20 rounded-full p-2 transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600"></div>
              </div>
            ) : (
              <>
                {showGuide && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="bg-gradient-to-br from-indigo-50 to-blue-50 border-2 border-indigo-200 rounded-xl p-5"
                  >
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <h3 className="text-lg font-bold text-indigo-900 flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-indigo-600" />
                        Guide du Dashboard Comité TI - Clients
                      </h3>
                      <button
                        onClick={() => setShowGuide(false)}
                        className="p-2 rounded-lg hover:bg-indigo-100 text-indigo-700 transition-colors"
                        aria-label="Fermer le guide"
                      >
                        <ChevronDown className="w-5 h-5 rotate-180" />
                      </button>
                    </div>
                    <div className="text-sm text-gray-800 space-y-3">
                      <p>
                        Ce dashboard démontre <strong>où en est chaque client</strong> dans l’évolution de leur <strong>PRIsME Client</strong> et de la <strong>gestion des réunions</strong> Comité TI.
                      </p>
                      <p>
                        Il permet de <strong>voir les actions à prendre</strong>, de <strong>s’assurer de la rigueur des rencontres</strong> et de <strong>garder les standards</strong> (participants, ordre du jour, notes, décisions, enjeux, risques, actions, résumé, checklist).
                      </p>
                      <p>
                        Ce dashboard est <strong>essentiel</strong> et constitue un <strong>outil opérationnel clé</strong> pour aider le Comité PRIsME à voir l’évolution chez les clients <strong>à très haut niveau</strong>.
                      </p>
                    </div>
                  </motion.div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <SummaryCard label="En cours" value={totals?.en_cours ?? 0} tone="blue" />
                  <SummaryCard label="Complétées" value={totals?.completees ?? 0} tone="green" />
                  <SummaryCard label="Validées" value={totals?.valides ?? 0} tone="emerald" />
                  <SummaryCard label="À valider" value={totals?.a_valider ?? 0} tone="amber" />
                </div>

                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="relative w-full md:w-[420px]">
                    <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Rechercher un client..."
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div className="text-sm text-gray-600">
                    {clientsSorted.length} client(s) affiché(s)
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-slate-100 border-b border-slate-200 text-xs font-bold text-slate-800 uppercase tracking-wide">
                    <div className="col-span-4">Client</div>
                    <div className="col-span-2">Responsable comité</div>
                    <div className="col-span-2 text-center">En cours</div>
                    <div className="col-span-2 text-center">Complétées</div>
                    <div className="col-span-1 text-center">Validées</div>
                    <div className="col-span-1 text-center">À valider</div>
                  </div>

                  {clientsSorted.length === 0 ? (
                    <div className="p-10 text-center text-gray-500">Aucun client ne correspond à la recherche.</div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {clientsSorted.map((c) => (
                        <ClientRow
                          key={c.client_id}
                          client={c}
                          partiesPrenantesBz={partiesPrenantesBz}
                          onOpen={() => setSelectedClient({ id: c.client_id, nom: c.client_nom })}
                          onResponsableChange={async (clientId, ppId) => {
                            try {
                              await setComiteClientResponsable(clientId, ppId);
                              setData((prev) => ({
                                ...prev!,
                                clients: (prev?.clients ?? []).map((cl) =>
                                  cl.client_id === clientId ? { ...cl, responsable_pp_id: ppId } : cl
                                ),
                              }));
                              setToast({ message: 'Responsable comité enregistré.', type: 'success' });
                            } catch (e) {
                              setToast({ message: 'Erreur lors de l\'enregistrement.', type: 'error' });
                            }
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </motion.div>
      </div>

      {/* Modal Envoyer un courriel (test) — visible seulement pour l'utilisateur autorisé */}
      <AnimatePresence>
        {showEmailTestModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
            onClick={() => !emailTestSending && setShowEmailTestModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-xl w-full max-w-md p-6"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-indigo-600" />
                Envoyer un courriel de test
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Choisissez une partie prenante BZ comme destinataire. Le courriel sera envoyé depuis votre compte Microsoft lié (ou SMTP).
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Partie prenante BZ (destinataire)</label>
                  <select
                    value={emailTestRecipientId === '' ? '' : String(emailTestRecipientId)}
                    onChange={(e) => setEmailTestRecipientId(e.target.value === '' ? '' : Number(e.target.value))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">— Choisir —</option>
                    {partiesPrenantesBz.map((pp) => (
                      <option key={pp.id} value={pp.id}>
                        {[pp.prenom, pp.nom].filter(Boolean).join(' ')} {pp.email ? `(${pp.email})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sujet</label>
                  <input
                    type="text"
                    value={emailTestSubject}
                    onChange={(e) => setEmailTestSubject(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Test Comité TI - PRIsME"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message (optionnel)</label>
                  <textarea
                    value={emailTestBody}
                    onChange={(e) => setEmailTestBody(e.target.value)}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Corps du courriel de test..."
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="include-table"
                    checked={emailTestIncludeTable}
                    onChange={(e) => setEmailTestIncludeTable(e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="include-table" className="text-sm text-gray-700">
                    Inclure le tableau Comité TI - Clients
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => !emailTestSending && setShowEmailTestModal(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  disabled={emailTestSending}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={sendTestEmail}
                  disabled={emailTestSending || !emailTestRecipientId}
                  className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {emailTestSending ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Envoi…
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Envoyer
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedClient && (
          <ComiteTIPrismeModal
            isOpen={true}
            onClose={() => setSelectedClient(null)}
            clientId={selectedClient.id}
            title={`🤝 Comité TI - ${selectedClient.nom}`}
            scope="CLIENT_TI"
          />
        )}
      </AnimatePresence>
    </>
  );
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'blue' | 'green' | 'emerald' | 'amber';
}) {
  const shell =
    tone === 'blue'
      ? 'border-blue-200 bg-blue-50 shadow-sm'
      : tone === 'green'
      ? 'border-green-200 bg-green-50 shadow-sm'
      : tone === 'emerald'
      ? 'border-emerald-200 bg-emerald-50 shadow-sm'
      : 'border-amber-300 bg-amber-50 shadow-sm';

  const valueClass =
    tone === 'blue'
      ? 'text-blue-900'
      : tone === 'green'
      ? 'text-green-900'
      : tone === 'emerald'
      ? 'text-emerald-900'
      : 'text-amber-950';

  return (
    <div className={`rounded-xl border-2 p-4 ${shell}`}>
      <div className="text-xs font-bold uppercase tracking-wide text-slate-800">{label}</div>
      <div className={`text-3xl font-extrabold mt-2 tabular-nums ${valueClass}`}>{value}</div>
    </div>
  );
}

interface ClientRowProps {
  client: ComiteDashboardClientSummary;
  partiesPrenantesBz: PartiePrenante[];
  onOpen: () => void;
  onResponsableChange: (clientId: number, partiePrenanteId: number) => void | Promise<void>;
}

function ClientRow({ client, partiesPrenantesBz, onOpen, onResponsableChange }: ClientRowProps) {
  const needsValidation = client.a_valider > 0;
  const responsablePpId = client.responsable_pp_id ?? '';

  return (
    <div className="w-full text-left grid grid-cols-12 gap-3 px-4 py-3 hover:bg-indigo-50/40 transition-colors items-center">
      <button
        onClick={onOpen}
        className="col-span-4 flex items-center gap-2 text-left"
      >
        <Users className="w-4 h-4 text-gray-500 shrink-0" />
        <div className="font-semibold text-gray-900">{client.client_nom}</div>
        {needsValidation && (
          <span className="ml-2 inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-800">
            <AlertTriangle className="w-3 h-3" />
            À valider
          </span>
        )}
        {!needsValidation && client.valides > 0 && (
          <span className="ml-2 inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-800">
            <CheckCircle2 className="w-3 h-3" />
            Validé
          </span>
        )}
      </button>

      <div className="col-span-2" onClick={(e) => e.stopPropagation()}>
        <select
          value={responsablePpId}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') return;
            void onResponsableChange(client.client_id, Number(v));
          }}
          className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          title="Partie prenante BZ responsable du comité"
        >
          <option value="">—</option>
          {partiesPrenantesBz.map((pp) => (
            <option key={pp.id} value={pp.id}>
              {[pp.prenom, pp.nom].filter(Boolean).join(' ').trim() || `Partie prenante #${pp.id}`}
            </option>
          ))}
        </select>
      </div>

      <div className="col-span-2 text-center font-semibold text-blue-700">{client.en_cours}</div>
      <div className="col-span-2 text-center font-semibold text-green-700">{client.completees}</div>
      <div className="col-span-1 text-center font-semibold text-emerald-700">{client.valides}</div>
      <div className="col-span-1 text-center">
        <span
          className={`inline-flex items-center justify-center min-w-8 px-2 py-1 rounded-full text-xs font-bold ${
            needsValidation ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-700'
          }`}
          title="Réunions avec checklist non entièrement validée"
        >
          <ClipboardCheck className="w-3 h-3 mr-1" />
          {client.a_valider}
        </span>
      </div>
    </div>
  );
}

